import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import useMe from '@app/hooks/auth/useMe';
import useTextAgentActions from '@app/hooks/text-agents/useTextAgentActions';
import useTextAgents from '@app/hooks/text-agents/useTextAgents';
import useModal from '@app/hooks/useModal';
import type { AITextAgent, AITextAgentListParams } from '@app/services/api.types';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import { Flex, type TableColumnsType } from 'antd';
import React, { useState } from 'react';
import { CgSoftwareDownload } from 'react-icons/cg';
import { HiOutlineArrowTopRightOnSquare, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';
import { MdAdd } from 'react-icons/md';
import { Link } from 'react-router';
import TextAgentDeleteModal from './components/TextAgentDeleteModal';
import { TextAgentEditModal } from './components/TextAgentEditModal';
import TextAgentFilters from './components/TextAgentFilters';
import TextAgentDrawer from './components/TextAgentDrawer';
import { TextAgentAddModal } from './components/TextAgentAddModal';

export const AiTextAgentsList = () => {
    const [selectedRecord, setSelectedRecord] = useState<AITextAgent | null>(null);
    const [params, setParams] = useState<AITextAgentListParams>({
        page: 1,
        per_page: 10,
        search: '',
        order_by: undefined,
        order_dir: undefined,
    });
    const { data: me } = useMe();
    const { data, isLoading } = useTextAgents(params);
    const { exportTextAgents } = useTextAgentActions();

    const modalAdd = useModal<AITextAgent>({
        initialData: undefined,
    });

    const modalEdit = useModal<AITextAgent>({
        initialData: undefined,
    });

    const modalRemove = useModal<AITextAgent>({
        initialData: undefined,
    });

    const columns: TableColumnsType<AITextAgent> = [
        {
            title: 'Agent Name',
            dataIndex: 'name',
            key: 'name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Model',
            dataIndex: 'model_name',
            key: 'model_name',
            render: (value) => value || '-',
            sorter: false,
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            sorter: false,
            width: 220,
            fixed: 'right',
            hidden: !me?.is_internal,
            render: (_value, record) => (
                <StyledButtonGroup justify="flex-start">
                    <StyledButton
                        icon={<HiOutlineArrowTopRightOnSquare />}
                        size="small"
                        color="green"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            handlePreviewWebchat(record);
                        }}>
                        Preview
                    </StyledButton>
                    <StyledButton
                        icon={<HiOutlinePencil />}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            modalEdit.open(record);
                        }}>
                        Edit
                    </StyledButton>
                    <StyledButton
                        icon={<HiOutlineTrash />}
                        size="small"
                        color="red"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            modalRemove.open(record);
                        }}>
                        Delete
                    </StyledButton>
                </StyledButtonGroup>
            ),
        },
    ];

    const closeEditModal = () => {
        modalEdit.reset();
    };

    const closeAddModal = () => {
        modalAdd.reset();
    };

    const closeRemoveModal = () => {
        modalRemove.reset();
    };

    const handleExport = async () => {
        exportTextAgents.mutate(params);
    };

    const handlePreviewWebchat = (record: AITextAgent) => {
        const link = `${globalThis.location.origin}${url.webchat(record.id)}`;
        window.open(link, '_blank', 'noopener,noreferrer');
    };

    return (
        <React.Fragment>
            <PageMeta title="AI Text Agents" />

            <PageHeader
                breadcrumbItems={[
                    {
                        title: <Link to={url.adminPortal.dashboard}>Dashboard</Link>,
                    },
                    { title: 'AI Text Agents' },
                ]}>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="AI Text Agents" />
                        <PageIntro>
                            <p>Manage AI text agents</p>
                        </PageIntro>
                    </div>
                    {me?.is_internal ? (
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    color="blue"
                                    icon={<MdAdd size={20} />}
                                    onClick={() => modalAdd.open()}>
                                    Add New
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    ) : null}
                </Flex>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <TextAgentFilters
                            onChange={(values) => {
                                setParams((prev) => ({ ...prev, ...values, page: 1 }));
                            }}
                        />
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    icon={<CgSoftwareDownload size={20} />}
                                    disabled={Boolean(!data?.total)}
                                    onClick={handleExport}>
                                    Export CSV
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<AITextAgent>
                        columns={columns}
                        dataSource={data?.data ?? []}
                        rowKey={({ id }) => id}
                        loading={isLoading}
                        pagination={{
                            current: params.page,
                            pageSize: params.limit,
                            total: data?.total ?? 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                order_by,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedRecord(record),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />
                </PageLayout.Content>
            </PageLayout>

            <TextAgentDrawer
                size={700}
                onClose={() => setSelectedRecord(null)}
                open={Boolean(selectedRecord)}
                data={selectedRecord}
            />

            <TextAgentAddModal
                open={modalAdd.isOpen}
                onCancel={closeAddModal}
                onOk={closeAddModal}
            />

            <TextAgentEditModal
                open={modalEdit.isOpen}
                data={modalEdit.data}
                onCancel={closeEditModal}
                onOk={closeEditModal}
            />

            <TextAgentDeleteModal
                open={modalRemove.isOpen}
                data={modalRemove.data}
                onCancel={closeRemoveModal}
                onOk={closeRemoveModal}
            />
        </React.Fragment>
    );
};

export default AiTextAgentsList;
